import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { Expander, ExpanderLayout } from "@amzn/stencil-react-components/expander";
import { Avatar, AvatarSize } from "@amzn/stencil-react-components/avatar";
import React, { FC, useEffect, useRef } from "react";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { formatCampaignEscalationDeadline } from "../campaigns.util";
import { buildTeamView } from "../campaigns-deep-link";
import { CampaignResponseForm } from "./campaign-response-form";
import { ResponseStatusChip } from "./response-status-chip";
import { TeamDeepLinkBanner } from "./team-deep-link-banner";
import type { DirectReportStatus } from "@/services/hardware-self-service/hardware-self-service.types";

interface TeamCampaignsSectionProps {
  directReports: DirectReportStatus[];
  currentUsername: string;
  linkedAlias?: string;
  isTeamListTruncated?: boolean;
}

interface TeamCampaignsReportProps {
  report: DirectReportStatus;
  currentUsername: string;
  isLinked: boolean;
}

const TeamCampaignsReport: FC<TeamCampaignsReportProps> = ({
  report,
  currentUsername,
  isLinked,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!isLinked || !card) {
      return;
    }
    const { top, bottom } = card.getBoundingClientRect();
    if (top < 0 || bottom > window.innerHeight) {
      card.scrollIntoView?.({ behavior: "smooth", block: "start" });
    }
  }, [isLinked]);

  return (
    <View ref={cardRef}>
      <Expander
        titleText={report.username}
        shouldExpandOnMount={isLinked}
        renderTitle={() => (
          <Row gridGap="S300" alignItems="center">
            <Avatar username={report.username} size={AvatarSize.ExtraSmall} showUsername={false} />
            <Text>{report.username}</Text>
            <Text fontWeight="bold">{report.status.campaign?.name}</Text>
            <Text color="neutral70">
              {getString("campaigns.respondBy", {
                date: formatCampaignEscalationDeadline(report.status.campaign),
              })}
            </Text>
            <ResponseStatusChip isResponseComplete={report.status.scope?.isResponseComplete} />
          </Row>
        )}
        layout={ExpanderLayout.Card}
      >
        <CampaignResponseForm
          campaign={report.status.campaign}
          assets={report.status.assets ?? []}
          existingResponses={report.status.responses ?? {}}
          existingPickupAddress={report.status.pickupAddress}
          login={report.username}
          currentUsername={currentUsername}
          scope={report.status.scope}
          showRespondByDate={false}
        />
      </Expander>
    </View>
  );
};

export const TeamCampaignsSection: FC<TeamCampaignsSectionProps> = ({
  directReports,
  currentUsername,
  linkedAlias,
  isTeamListTruncated,
}) => {
  const { reports, matchedUsername, unmatchedAlias } = buildTeamView(directReports, linkedAlias);

  if (reports.length === 0) {
    return null;
  }

  return (
    <Col gridGap="S300">
      {unmatchedAlias && (
        <TeamDeepLinkBanner
          unmatchedAlias={unmatchedAlias}
          isTeamListTruncated={isTeamListTruncated}
        />
      )}
      {reports.map((report) => (
        <TeamCampaignsReport
          key={report.username}
          report={report}
          currentUsername={currentUsername}
          isLinked={report.username === matchedUsername}
        />
      ))}
    </Col>
  );
};
